import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Loader2, ShieldAlert, Clock, Info, KeyRound, RefreshCw } from 'lucide-react';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [errorType, setErrorType] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPasswordPolicy, setShowPasswordPolicy] = useState(false);
  
  // 2FA state
  const [show2FA, setShow2FA] = useState(false);
  const [twoFACode, setTwoFACode] = useState('');
  const [pendingToken, setPendingToken] = useState('');
  const [resending, setResending] = useState(false);
  
  const { login, verify2FA, resend2FACode } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setErrorType('');
    setLoading(true);

    try {
      const result = await login(email, password);
      
      // Check if 2FA is required
      if (result?.requires_2fa) {
        setShow2FA(true);
        setPendingToken(result.pending_token);
        setError('');
      } else {
        navigate('/suivi');
      }
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail || 'Erreur de connexion';
      
      if (status === 429) {
        setErrorType('blocked');
        setError(detail);
      } else if (detail.includes('tentative')) {
        setErrorType('attempts');
        setError(detail);
      } else {
        setErrorType('general');
        setError(detail);
      }
    } finally {
      setLoading(false);
    }
  };

  const handle2FASubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await verify2FA(email, twoFACode, pendingToken);
      navigate('/suivi');
    } catch (err) {
      setError(err.response?.data?.detail || 'Code invalide');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setResending(true);
    try {
      await resend2FACode(pendingToken);
      setError('');
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors du renvoi');
    } finally {
      setResending(false);
    }
  };

  // 2FA verification screen
  if (show2FA) {
    return (
      <div className="login-container">
        <div className="login-card">
          <div className="text-center mb-8">
            <div className="mx-auto w-16 h-16 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
              <KeyRound className="h-8 w-8 text-primary" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-2">
              Vérification à deux facteurs
            </h1>
            <p className="text-sm text-slate-500">
              Un code de vérification a été envoyé à<br />
              <span className="font-medium text-slate-700">{email}</span>
            </p>
          </div>

          <form onSubmit={handle2FASubmit} className="space-y-6">
            {error && (
              <Alert variant="destructive" data-testid="2fa-error">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <div className="space-y-2">
              <Label htmlFor="code">Code de vérification</Label>
              <Input
                id="code"
                type="text"
                value={twoFACode}
                onChange={(e) => setTwoFACode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                required
                className="rounded-lg text-center text-2xl tracking-widest font-mono"
                maxLength={6}
                data-testid="2fa-code-input"
              />
              <p className="text-xs text-slate-500 text-center">
                Entrez le code à 6 chiffres
              </p>
            </div>

            <Button
              type="submit"
              disabled={loading || twoFACode.length !== 6}
              className="w-full rounded-lg bg-primary hover:bg-primary/90"
              data-testid="2fa-submit-button"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Vérification...
                </>
              ) : (
                'Vérifier'
              )}
            </Button>

            <div className="flex items-center justify-between text-sm">
              <button
                type="button"
                onClick={() => {
                  setShow2FA(false);
                  setTwoFACode('');
                  setPendingToken('');
                  setError('');
                }}
                className="text-slate-500 hover:text-slate-700"
              >
                ← Retour
              </button>
              <button
                type="button"
                onClick={handleResend}
                disabled={resending}
                className="text-primary hover:text-primary/80 flex items-center gap-1"
                data-testid="resend-code-button"
              >
                {resending ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  <RefreshCw className="h-3 w-3" />
                )}
                Renvoyer le code
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  // Normal login screen
  return (
    <div className="login-container">
      <div className="login-card">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-slate-900 mb-2">
            Performance Transmission
          </h1>
          <p className="text-sm text-slate-500">
            Connectez-vous à votre compte
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {error && (
            <Alert 
              variant="destructive" 
              data-testid="login-error"
              className={`${errorType === 'blocked' ? 'bg-red-100 border-red-500' : ''}`}
            >
              <div className="flex items-start gap-2">
                {errorType === 'blocked' ? (
                  <Clock className="h-4 w-4 mt-0.5 flex-shrink-0" />
                ) : errorType === 'attempts' ? (
                  <ShieldAlert className="h-4 w-4 mt-0.5 flex-shrink-0" />
                ) : null}
                <AlertDescription>{error}</AlertDescription>
              </div>
            </Alert>
          )}

          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="votre@email.ch"
              required
              className="rounded-lg"
              data-testid="login-email-input"
              disabled={errorType === 'blocked'}
            />
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="password">Mot de passe</Label>
              <button
                type="button"
                className="text-xs text-slate-500 hover:text-primary flex items-center gap-1"
                onClick={() => setShowPasswordPolicy(!showPasswordPolicy)}
                data-testid="password-policy-toggle"
              >
                <Info className="h-3 w-3" />
                Politique
              </button>
            </div>
            
            {showPasswordPolicy && (
              <div className="text-xs text-slate-500 bg-slate-50 p-3 rounded-lg space-y-1" data-testid="password-policy">
                <p className="font-medium text-slate-700">Exigences du mot de passe :</p>
                <ul className="list-disc list-inside space-y-0.5">
                  <li>Minimum 12 caractères</li>
                  <li>Au moins une majuscule (A-Z)</li>
                  <li>Au moins une minuscule (a-z)</li>
                  <li>Au moins un chiffre (0-9)</li>
                  <li>Au moins un symbole (!@#$%^&*...)</li>
                </ul>
              </div>
            )}
            
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••••••"
              required
              className="rounded-lg"
              data-testid="login-password-input"
              disabled={errorType === 'blocked'}
            />
          </div>

          <Button
            type="submit"
            disabled={loading || errorType === 'blocked'}
            className="w-full rounded-lg bg-primary hover:bg-primary/90"
            data-testid="login-submit-button"
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Connexion...
              </>
            ) : errorType === 'blocked' ? (
              'Compte temporairement bloqué'
            ) : (
              'Se connecter'
            )}
          </Button>
          
          {errorType === 'blocked' && (
            <p className="text-xs text-center text-slate-500">
              Veuillez réessayer dans 15 minutes ou contactez l'administrateur.
            </p>
          )}
        </form>
      </div>
    </div>
  );
};

export default Login;
